// @migration-in-progress
import React, { useState, useEffect } from 'react';
import { Fuel, Calendar, Download, Filter, TrendingUp } from 'lucide-react';
import { useVessel } from 'contexts/VesselContext';
import DESIGN from '../designSystem';

/**
 * BunkeringRecords - View and analyze all fuel records
 * Provides filtering, totals, and export capabilities
 */
const BunkeringRecords = ({ onClose }) => {
  const { currentVesselId, getCurrentVessel } = useVessel()
  const currentVessel = getCurrentVessel();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [filterType, setFilterType] = useState('all'); // all, receive, transfer, consume
  const [filterPeriod, setFilterPeriod] = useState('month'); // week, month, year, all
  const [showStats, setShowStats] = useState(true);

  // Load all bunkering records from localStorage
  useEffect(() => {
    const loadRecords = () => {
      const allRecords = [];
      
      // Get all localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        
        // Find bunkering records
        if (key && key.startsWith('bunkering_')) {
          try {
            const record = JSON.parse(localStorage.getItem(key));
            allRecords.push(record);
          } catch (e) {

          }
        }
      }
      
      // Sort by timestamp, newest first
      allRecords.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecords(allRecords);
      setFilteredRecords(allRecords);
    };

    loadRecords();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...records];
    
    // Filter by operation type
    if (filterType !== 'all') {
      filtered = filtered.filter(r => r.operationType === filterType);
    }
    
    // Filter by time period
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (filterPeriod) {
      case 'week':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      default: // 'all'
        cutoffDate.setFullYear(2000); // Include everything
    }
    
    filtered = filtered.filter(r => new Date(r.timestamp) >= cutoffDate);
    setFilteredRecords(filtered);
  }, [filterType, filterPeriod, records]);

  // Calculate statistics
  const calculateStats = () => {
    const stats = {
      totalReceived: 0,
      totalConsumed: 0,
      totalTransferred: 0,
      dieselReceived: 0,
      petrolReceived: 0,
      suppliers: new Set(),
      ports: new Set()
    };

    filteredRecords.forEach(record => {
      const qty = parseFloat(record.quantity) || 0;
      
      // Convert gallons to litres for consistency
      const qtyInLitres = record.unit === 'gallons' ? qty * 3.78541 : qty;
      
      if (record.operationType === 'receive') {
        stats.totalReceived += qtyInLitres;
        if (record.fuelType === 'diesel') stats.dieselReceived += qtyInLitres;
        if (record.fuelType === 'petrol') stats.petrolReceived += qtyInLitres;
        if (record.supplier) stats.suppliers.add(record.supplier);
      } else if (record.operationType === 'consume') {
        stats.totalConsumed += qtyInLitres;
      } else if (record.operationType === 'transfer') {
        stats.totalTransferred += qtyInLitres;
      }
      
      if (record.port) stats.ports.add(record.port);
    });

    return stats;
  };

  const stats = calculateStats();

  // Format date for display
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'Operation', 'Fuel Type', 'Quantity', 'Unit', 'Supplier', 'Delivery Note', 'Port', 'Responsible', 'Notes'];
    
    const csvData = filteredRecords.map(r => [
      formatDate(r.timestamp),
      formatTime(r.timestamp),
      r.operationType,
      r.fuelType,
      r.quantity,
      r.unit,
      r.supplier || '',
      r.deliveryNote || '',
      r.port || '',
      r.responsiblePerson || '',
      r.notes || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bunkering_records_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className={DESIGN.header.padding + ' ' + DESIGN.header.borderBottom}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Fuel className="h-5 w-5 text-yellow-600" />
            <div>
              <h3 className={DESIGN.header.title}>Bunkering Records</h3>
              <p className={DESIGN.header.subtitle}>
                Complete fuel operation history
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToCSV}
              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={DESIGN.body.padding}>
        {/* Filters */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Filters</span>
            </div>
            <button
              onClick={() => setShowStats(!showStats)}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              {showStats ? 'Hide' : 'Show'} Statistics
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Operation Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              >
                <option value="all">All Operations</option>
                <option value="receive">Received Only</option>
                <option value="transfer">Transfers Only</option>
                <option value="consume">Consumption Only</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Time Period</label>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              >
                <option value="week">Last Week</option>
                <option value="month">Last Month</option>
                <option value="year">Last Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStats && (
          <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-900">Total Received</span>
              </div>
              <p className="text-lg font-bold text-blue-900">
                {stats.totalReceived.toFixed(0)} L
              </p>
              {stats.dieselReceived > 0 && (
                <p className="text-xs text-blue-700">
                  Diesel: {stats.dieselReceived.toFixed(0)} L
                </p>
              )}
            </div>
            
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-orange-600" />
                <span className="text-xs font-medium text-orange-900">Total Consumed</span>
              </div>
              <p className="text-lg font-bold text-orange-900">
                {stats.totalConsumed.toFixed(0)} L
              </p>
            </div>
            
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-green-900">Unique Suppliers</span>
              </div>
              <p className="text-lg font-bold text-green-900">
                {stats.suppliers.size}
              </p>
            </div>
            
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-900">Ports</span>
              </div>
              <p className="text-lg font-bold text-purple-900">
                {stats.ports.size}
              </p>
            </div>
          </div>
        )}

        {/* Records Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2">Date/Time</th>
                <th className="text-left py-2 px-2">Operation</th>
                <th className="text-left py-2 px-2">Type</th>
                <th className="text-right py-2 px-2">Quantity</th>
                <th className="text-left py-2 px-2">Supplier/Port</th>
                <th className="text-left py-2 px-2">DN/Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No bunkering records found for selected filters
                  </td>
                </tr>
              ) : (
                filteredRecords.map((record, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-2">
                      <div className="text-xs">
                        <div className="font-medium">{formatDate(record.timestamp)}</div>
                        <div className="text-gray-500">{formatTime(record.timestamp)}</div>
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        record.operationType === 'receive' ? 'bg-green-100 text-green-700' :
                        record.operationType === 'transfer' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {record.operationType}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-xs">
                      {record.fuelType}
                    </td>
                    <td className="py-2 px-2 text-right font-medium">
                      {record.quantity} {record.unit}
                    </td>
                    <td className="py-2 px-2 text-xs">
                      {record.supplier && <div>{record.supplier}</div>}
                      {record.port && <div className="text-gray-500">{record.port}</div>}
                    </td>
                    <td className="py-2 px-2 text-xs">
                      {record.deliveryNote && <div className="font-medium">{record.deliveryNote}</div>}
                      {record.notes && <div className="text-gray-500 truncate max-w-xs">{record.notes}</div>}
                      {record.samplesTaken && (
                        <span className="text-green-600 font-medium">✓ Samples</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        {filteredRecords.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
            Showing {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''} 
            {filterPeriod !== 'all' && ` from last ${filterPeriod}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default BunkeringRecords;